import { z } from 'zod';
import { SCOPE_TYPE_VALUES, USER_STATUS_VALUES } from '@fas/types';
import { emailSchema, optionalText, paginationQuerySchema, uuidSchema } from './common';

/** Upper bound protects the password hasher from denial-of-service inputs. */
export const PASSWORD_MAX_LENGTH = 128;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string('Password is required').min(1, 'Password is required').max(PASSWORD_MAX_LENGTH),
  deviceId: z.string().trim().max(200).optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({ email: emailSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z.string('Reset token is required').trim().min(20, 'Invalid reset token').max(200),
  newPassword: z.string('New password is required').min(1, 'New password is required').max(PASSWORD_MAX_LENGTH),
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// ── Two-step sign-in (§49, ADR-068) ────────────────────────────────────────────
/** A six-digit authenticator code, or a recovery code (letters, digits, a dash). */
export const secondFactorCodeSchema = z
  .string('Enter the code')
  .trim()
  .min(6, 'Enter the 6-digit code or a recovery code')
  .max(20, 'Enter the 6-digit code or a recovery code')
  .regex(/^[0-9A-Za-z -]+$/, 'Enter the 6-digit code or a recovery code');
/** A six-digit code from the authenticator app. */
export const totpCodeSchema = z.string('Enter the code').trim().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app');

/** The second step of a password sign-in. */
export const loginMfaSchema = z.object({ mfaToken: z.string().min(20).max(500), code: secondFactorCodeSchema, deviceId: z.string().trim().max(200).optional() }).strict();
export type LoginMfaInput = z.infer<typeof loginMfaSchema>;

/** Starting the set-up asks for the password again. */
export const mfaSetupSchema = z.object({ password: z.string('Password is required').min(1, 'Password is required').max(PASSWORD_MAX_LENGTH) }).strict();
export type MfaSetupInput = z.infer<typeof mfaSetupSchema>;

/** A code from the authenticator app (confirming the set-up, new recovery codes). */
export const mfaCodeSchema = z.object({ code: totpCodeSchema }).strict();
export type MfaCodeInput = z.infer<typeof mfaCodeSchema>;

/** Turning it off asks for the password and a code (or a recovery code). */
export const mfaDisableSchema = z.object({ password: z.string('Password is required').min(1, 'Password is required').max(PASSWORD_MAX_LENGTH), code: secondFactorCodeSchema }).strict();
export type MfaDisableInput = z.infer<typeof mfaDisableSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required').max(PASSWORD_MAX_LENGTH),
  newPassword: z.string().min(1, 'New password is required').max(PASSWORD_MAX_LENGTH),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ── Password policy (§49, §70 Security) ────────────────────────────────────────
export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireDigit: boolean;
  requireSymbol: boolean;
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 10,
  requireUppercase: true,
  requireLowercase: true,
  requireDigit: true,
  requireSymbol: false,
};

export const passwordPolicySchema = z.object({
  minLength: z.number().int().min(8).max(PASSWORD_MAX_LENGTH),
  requireUppercase: z.boolean(),
  requireLowercase: z.boolean(),
  requireDigit: z.boolean(),
  requireSymbol: z.boolean(),
});

const COMMON_PASSWORDS = new Set([
  'password',
  'password1',
  'password123',
  'passw0rd',
  '123456789',
  '1234567890',
  'qwerty123',
  'qwertyuiop',
  'iloveyou',
  'welcome123',
  'admin123',
  'letmein123',
  'changeme',
  'changeme123',
]);

/**
 * Returns human-readable policy violations (empty array = acceptable). The API is the
 * authority; the web app uses the same function to give immediate feedback.
 */
export function checkPasswordPolicy(
  password: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY,
  context: { email?: string | null } = {},
): string[] {
  const problems: string[] = [];
  if (password.length < policy.minLength) {
    problems.push(`Password must be at least ${policy.minLength} characters long`);
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    problems.push(`Password must be at most ${PASSWORD_MAX_LENGTH} characters long`);
  }
  if (policy.requireUppercase && !/[A-Z]/.test(password)) {
    problems.push('Password must contain an uppercase letter');
  }
  if (policy.requireLowercase && !/[a-z]/.test(password)) {
    problems.push('Password must contain a lowercase letter');
  }
  if (policy.requireDigit && !/\d/.test(password)) {
    problems.push('Password must contain a digit');
  }
  if (policy.requireSymbol && !/[^A-Za-z0-9]/.test(password)) {
    problems.push('Password must contain a symbol');
  }
  const lowered = password.toLowerCase();
  if (COMMON_PASSWORDS.has(lowered)) {
    problems.push('Password is too common');
  }
  const email = context.email?.toLowerCase();
  if (email) {
    const local = email.split('@')[0];
    if (lowered === email || (local.length >= 4 && lowered.includes(local))) {
      problems.push('Password must not contain your email address');
    }
  }
  return problems;
}

// ── User administration ────────────────────────────────────────────────────────
export const roleAssignmentSchema = z
  .object({
    roleId: uuidSchema,
    scopeType: z.enum(SCOPE_TYPE_VALUES),
    collegeId: uuidSchema.nullable().optional(),
    departmentId: uuidSchema.nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.scopeType === 'COLLEGE' && !v.collegeId) {
      ctx.addIssue({ code: 'custom', path: ['collegeId'], message: 'A college is required for college scope' });
    }
    if (v.scopeType === 'DEPARTMENT' && !v.departmentId) {
      ctx.addIssue({
        code: 'custom',
        path: ['departmentId'],
        message: 'A department is required for department scope',
      });
    }
    if (v.scopeType === 'INSTITUTION' && (v.collegeId || v.departmentId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['scopeType'],
        message: 'Institution scope must not specify a college or department',
      });
    }
    if (v.scopeType === 'COLLEGE' && v.departmentId) {
      ctx.addIssue({
        code: 'custom',
        path: ['departmentId'],
        message: 'College scope must not specify a department',
      });
    }
  });
export type RoleAssignmentInput = z.infer<typeof roleAssignmentSchema>;

export const createUserSchema = z
  .object({
    email: emailSchema,
    displayName: optionalText(200),
    /** Optional initial password; when omitted a password-setup email is sent. */
    password: z.string().min(1).max(PASSWORD_MAX_LENGTH).optional(),
    roles: z.array(roleAssignmentSchema).max(20).default([]),
  });
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  displayName: optionalText(200),
  status: z.enum(USER_STATUS_VALUES).optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const listUsersQuerySchema = paginationQuerySchema.extend({
  status: z.enum(USER_STATUS_VALUES).optional(),
  roleCode: z.string().trim().max(50).optional(),
});
export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;

// ── Roles & permissions ────────────────────────────────────────────────────────
export const createRoleSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]{1,49}$/, 'Role code must be 2-50 characters: A-Z, 0-9 and underscore'),
  name: z.string().trim().min(1, 'Name is required').max(100),
  description: optionalText(500),
});
export type CreateRoleInput = z.infer<typeof createRoleSchema>;

export const updateRoleSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  description: optionalText(500),
  active: z.boolean().optional(),
});
export type UpdateRoleInput = z.infer<typeof updateRoleSchema>;

export const setRolePermissionsSchema = z.object({
  permissionCodes: z.array(z.string().trim().min(1).max(100)).max(200),
});
export type SetRolePermissionsInput = z.infer<typeof setRolePermissionsSchema>;
